import React, { useEffect, useState } from 'react';
import {
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
  Dropdown,
  Menu,
  message,
  Modal,
  Pagination,
  Spin,
} from 'antd';
import {
  getLottoList,
  LottoList,
  LottoListParams,
  updateLottoStatus,
  getLottoDetail,
  addOrEditLottoActivity,
} from '@/api/lottoActivity';

import { useNavigate } from 'react-router-dom';
import { PageLink } from '@/config/pageConfig';

const { Option } = Select;

const avatarColors = ['#FFBF00', '#FF8800', '#8FD600', '#39B8EE'];

const statusMap: Record<number, string> = {
  0: '未开始',
  1: '进行中',
  2: '已结束',
};

const typeMap: Record<number, string> = {
  0: '扭蛋券抽奖',
  1: '盲盒抽奖',
};

const getIconChar = (name?: string) => name?.charAt(0);

const getStableColor = (key: string) => {
  const hash = [...key].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};

interface Props {
  active: boolean;
}

const LottoActivityTab: React.FC<Props> = ({ active }) => {
  useEffect(() => {
    if (active) {
      fetchData();
    }
  }, [active]);

  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<LottoListParams>({
    keyword: '',
    activityStatus: undefined,
  });
  const [params, setParams] = useState<LottoListParams>({
    keyword: '',
    activityStatus: undefined,
    currentPage: 1,
    pageSize: 8,
  });
  const [list, setList] = useState<LottoList[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchData();
  }, [params]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getLottoList(params);
      if (res.success) {
        setList(res.data?.records || []);
        setTotal(res.data?.total || 0);
      } else {
        message.error(res.message || '获取失败');
      }
    } catch {
      message.error('请求错误');
    } finally {
      setLoading(false);
    }
  };

  const handleQuery = () => {
    setParams({
      ...formValues,
      currentPage: 1,
      pageSize: 8,
    });
  };

  const handleReset = () => {
    setFormValues({
      keyword: '',
      activityStatus: undefined,
    });
    setParams({
      keyword: '',
      activityStatus: undefined,
      currentPage: 1,
      pageSize: 8,
    });
  };

  const handleUpdateStatus = (id: number, currentStatus: number) => {
    const targetStatus = currentStatus === 1 ? 0 : 1;
    const actionText = targetStatus === 1 ? '上架' : '下架';
    Modal.confirm({
      title: `确认${actionText}该活动？`,
      okText: `确认${actionText}`,
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await updateLottoStatus(id, targetStatus);
          if (res.success) {
            message.success(`${actionText}成功`);
            fetchData(); // 刷新列表
          } else {
            message.error(res.message || `${actionText}失败`);
          }
        } catch {
          message.error(`${actionText}异常`);
        }
      },
    });
  };

  // 递归将所有名为 "id" 的字段设为 null
  const deepNullifyIds = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(deepNullifyIds);
    } else if (obj !== null && typeof obj === 'object') {
      const newObj: any = {};
      for (const key in obj) {
        if (key === 'id') {
          newObj[key] = null;
        } else {
          newObj[key] = deepNullifyIds(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const renderMoreMenu = (activityId: any) => (
    <Menu
      onClick={async ({ key }) => {
        if (key === 'prize') {
          if (!activityId) {
            message.error('活动ID缺失，无法跳转');
            return;
          }
          navigate(
            `${PageLink.LotteryActivity}/record?activityId=${activityId}`
          );
        } else if (key === 'copy') {
          try {
            if (!activityId) {
              message.error('活动ID缺失，无法复制');
              return;
            }
            const res = await getLottoDetail({ id: activityId });
            if (!res.success || !res.data) {
              message.error(res.message || '获取活动详情失败');
              return;
            }
            const clonedData = deepNullifyIds(res.data);
            const copiedData = {
              ...clonedData,
              activityName: clonedData.activityName + '（副本）',
            };
            const addRes = await addOrEditLottoActivity(copiedData);
            if (addRes.success) {
              message.success('复制成功');
              fetchData(); // 刷新列表
            } else {
              message.error(addRes.message || '复制失败');
            }
          } catch (error) {
            message.error('复制操作异常');
          }
        }
      }}
      items={[
        { key: 'prize', label: '中奖记录' },
        { key: 'copy', label: '复制' },
      ]}
    />
  );

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <div style={{ flex: 1 }}>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="活动标题"
            allowClear
            value={formValues.keyword}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, keyword: e.target.value }))
            }
          />
          <Select
            placeholder="活动状态"
            allowClear
            style={{ width: 120 }}
            value={formValues.activityStatus}
            onChange={(value) =>
              setFormValues((prev) => ({ ...prev, activityStatus: value }))
            }
          >
            <Option value={0}>未开始</Option>
            <Option value={1}>进行中</Option>
            <Option value={2}>已结束</Option>
          </Select>
          <Button type="primary" onClick={handleQuery}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        <Spin spinning={loading}>
          <Row gutter={[25, 25]}>
            {/* 新增卡片 */}
            <Col span={8}>
              <div
                onClick={() => navigate(`${PageLink.LotteryActivity}/create`)}
                style={{
                  height: 184,
                  borderRadius: 2,
                  border: '1px dashed #1890ff',
                  color: '#1890ff',
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                <span style={{ fontSize: 22, marginRight: 4 }}>＋</span>{' '}
                新增活动
              </div>
            </Col>
            {/* 活动卡片列表 */}
            {list.map((item) => (
              <Col key={item.id} span={8}>
                <div
                  style={{
                    height: 184,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #eee',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  {/* 信息区 */}
                  <div
                    style={{
                      flex: '1 1 auto',
                      display: 'flex',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '16px 20px',
                        display: 'flex',
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {/* 左侧 icon */}
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: getStableColor(
                            item.activityName || ''
                          ),
                          color: '#fff',
                          fontSize: 22,
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: 12,
                          flexShrink: 0,
                        }}
                      >
                        {getIconChar(item.activityName)}
                      </div>

                      {/* 右侧内容 */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div
                          style={{
                            fontWeight: 500,
                            marginBottom: 4,
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {item.activityName}
                        </div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          活动时间：
                          <br />
                          {item.startTime} - {item.endTime}
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                          <span
                            style={{
                              backgroundColor:
                                item.activityStatus === 0
                                  ? '#FFE5CC'
                                  : item.activityStatus === 1
                                    ? '#D1F1CC'
                                    : '#F0F0F0',
                              color:
                                item.activityStatus === 0
                                  ? '#FF7F00'
                                  : item.activityStatus === 1
                                    ? '#3CB043'
                                    : '#999',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 12,
                            }}
                          >
                            {statusMap[item.activityStatus ?? 0]}
                          </span>
                          <span
                            style={{
                              backgroundColor: '#F5F5F5',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 12,
                              color: '#666',
                            }}
                          >
                            {typeMap[item.type ?? 0]}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 按钮区 */}
                  <div
                    style={{
                      height: 38,
                      backgroundColor: '#F5F5F5',
                      borderTop: '1px solid #eee',
                      padding: '0 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                      fontSize: 13,
                      color: '#999',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {item.status === 1 ? (
                      <>
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(
                              `${PageLink.LotteryActivity}/create?id=${item.id}`
                            )
                          }
                        >
                          查看活动
                        </span>
                        <span style={{ color: '#ccc' }}>已上架</span>
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            handleUpdateStatus(item.id!, item.status!)
                          }
                        >
                          下架活动
                        </span>
                        <Dropdown overlay={renderMoreMenu(item.id)}>
                          <span style={{ cursor: 'pointer' }}>更多</span>
                        </Dropdown>
                      </>
                    ) : (
                      <>
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(
                              `${PageLink.LotteryActivity}/create?id=${item.id}`
                            )
                          }
                        >
                          查看活动
                        </span>
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            handleUpdateStatus(item.id!, item.status!)
                          }
                        >
                          上架活动
                        </span>
                        <span style={{ color: '#ccc' }}>已下架</span>
                        <Dropdown overlay={renderMoreMenu(item.id)}>
                          <span style={{ cursor: 'pointer' }}>更多</span>
                        </Dropdown>
                      </>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Spin>
      </div>
      <div>
        <Pagination
          style={{ marginTop: 24, textAlign: 'right' }}
          current={params.currentPage}
          pageSize={params.pageSize}
          total={total}
          showSizeChanger={false}
          onChange={(page) =>
            setParams((prev) => ({
              ...prev,
              currentPage: page,
            }))
          }
        />
      </div>
    </div>
  );
};

export default LottoActivityTab;
